import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Blocks,
  BookOpen,
  Bot,
  ChevronRight,
  Gauge,
  LayoutGrid,
  LogOut,
  Menu as MenuIcon,
  Server,
  ShieldCheck,
  UserRound,
  X,
} from 'lucide-react';
import { ROLE_LABELS } from '@zentera/shared';
import { cn, Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from '@zentera/ui';
import { api } from '../lib/api';
import { meQueryKey, useMe } from '../lib/auth';
import type { Overview } from '../lib/types';
import { Logo } from './brand';

/* ───────────── Breadcrumbs ───────────── */

export interface Crumb {
  label: string;
  to?: string;
}

const CrumbContext = createContext<(c: Crumb[]) => void>(() => {});

/** Define as breadcrumbs da página atual. */
export function useBreadcrumbs(crumbs: Crumb[]) {
  const set = useContext(CrumbContext);
  const key = JSON.stringify(crumbs);
  useEffect(() => {
    set(JSON.parse(key) as Crumb[]);
    const last = (JSON.parse(key) as Crumb[]).at(-1);
    document.title = last ? `${last.label} · Zentera Admin` : 'Zentera Admin';
  }, [key, set]);
}

/* ───────────── Navegação ───────────── */

const NAV: { group: string; items: { to: string; label: string; icon: ReactNode; end?: boolean }[] }[] = [
  {
    group: 'Operação',
    items: [
      { to: '/', label: 'Visão geral', icon: <LayoutGrid />, end: true },
      { to: '/providers', label: 'Provedores', icon: <Blocks /> },
      { to: '/infrastructure', label: 'Infraestrutura', icon: <Server /> },
    ],
  },
  {
    group: 'Inteligência',
    items: [
      { to: '/agents', label: 'Agentes', icon: <Bot /> },
      { to: '/knowledge', label: 'Conhecimento global', icon: <BookOpen /> },
      { to: '/usage', label: 'Consumo de IA', icon: <Gauge /> },
    ],
  },
  {
    group: 'Plataforma',
    items: [
      { to: '/integrations', label: 'Integrações', icon: <Activity /> },
      { to: '/security', label: 'Segurança e auditoria', icon: <ShieldCheck /> },
    ],
  },
];

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { data: overview } = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.get<Overview>('/api/overview'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const attention = overview ? overview.instances.offline + overview.instances.degraded : 0;

  return (
    <nav aria-label="Principal" className="flex h-full flex-col">
      <div className="flex h-12 shrink-0 items-center px-4">
        <Link to="/" onClick={onNavigate} className="rounded-sm">
          <Logo height={20} />
        </Link>
        <span className="ml-2 rounded-sm border border-line px-1 text-2xs font-medium tracking-wide text-fg-3 uppercase">Admin</span>
      </div>
      <div className="flex-1 space-y-5 overflow-y-auto px-2 pt-3 pb-4">
        {NAV.map((g) => (
          <div key={g.group}>
            <div className="px-2 pb-1 text-2xs font-medium tracking-wide text-fg-4 uppercase">{g.group}</div>
            <ul className="space-y-px">
              {g.items.map((it) => (
                <li key={it.to}>
                  <NavLink
                    to={it.to}
                    end={it.end}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'flex h-7 items-center gap-2.5 rounded-md px-2 text-sm transition-colors duration-75 [&_svg]:size-[15px] [&_svg]:shrink-0',
                        isActive
                          ? 'bg-bg font-medium text-fg shadow-xs ring-1 ring-line [&_svg]:text-brand'
                          : 'text-fg-2 hover:bg-muted hover:text-fg [&_svg]:text-fg-3',
                      )
                    }
                  >
                    {it.icon}
                    <span className="flex-1 truncate">{it.label}</span>
                    {it.to === '/infrastructure' && attention > 0 && (
                      <span className="tabular rounded-sm bg-danger-subtle px-1 text-2xs font-medium text-danger" aria-label={`${attention} instâncias requerem atenção`}>
                        {attention}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-line px-4 py-3 text-2xs text-fg-4">
        Zentera Admin · v0.1.0
      </div>
    </nav>
  );
}

function UserMenu() {
  const { data } = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSettled: () => {
      qc.clear();
      void qc.invalidateQueries({ queryKey: meQueryKey });
      void navigate('/login');
    },
  });
  if (!data) return null;
  const initials = data.user.name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  return (
    <Menu>
      <MenuTrigger className="flex items-center gap-2 rounded-md py-1 pr-1.5 pl-1 hover:bg-muted" aria-label="Conta">
        <span className="flex size-6 items-center justify-center rounded-full bg-fg text-2xs font-medium text-white">{initials}</span>
        <span className="hidden text-sm text-fg-2 md:inline">{data.user.name}</span>
      </MenuTrigger>
      <MenuContent>
        <MenuLabel>
          <div className="text-sm font-medium text-fg">{data.user.name}</div>
          <div>{data.user.email}</div>
          <div className="mt-1">{ROLE_LABELS[data.user.role]}</div>
        </MenuLabel>
        <MenuSeparator />
        <MenuItem icon={<UserRound />} onSelect={() => void navigate('/security/account')}>
          Minha conta e sessões
        </MenuItem>
        <MenuItem icon={<LogOut />} onSelect={() => logout.mutate()}>
          Sair
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

export function AppShell() {
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <CrumbContext.Provider value={setCrumbs}>
      <a href="#conteudo" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-bg focus:px-3 focus:py-2 focus:shadow-pop">
        Pular para o conteúdo
      </a>
      <div className="flex min-h-screen bg-bg">
        <aside className="sticky top-0 hidden h-screen w-[232px] shrink-0 border-r border-line bg-subtle lg:block">
          <Sidebar />
        </aside>

        {mobileOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-fg/20 animate-fade-in" onClick={() => setMobileOpen(false)} />
            <aside className="absolute inset-y-0 left-0 w-[260px] border-r border-line bg-subtle shadow-pop animate-pop-in">
              <button className="absolute top-3 right-3 rounded-md p-1 text-fg-3 hover:bg-muted" onClick={() => setMobileOpen(false)} aria-label="Fechar menu">
                <X className="size-4" />
              </button>
              <Sidebar onNavigate={() => setMobileOpen(false)} />
            </aside>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-12 shrink-0 items-center gap-3 border-b border-line bg-bg/95 px-4 backdrop-blur-sm lg:px-6">
            <button className="-ml-1 rounded-md p-1.5 text-fg-2 hover:bg-muted lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Abrir menu">
              <MenuIcon className="size-4" />
            </button>
            <nav aria-label="Trilha" className="min-w-0 flex-1">
              <ol className="flex min-w-0 items-center gap-1 text-sm">
                {crumbs.map((c, i) => {
                  const last = i === crumbs.length - 1;
                  return (
                    <li key={i} className={cn('flex min-w-0 items-center gap-1', !last && 'hidden sm:flex')}>
                      {i > 0 && <ChevronRight className="hidden size-3.5 shrink-0 text-fg-4 sm:block" aria-hidden="true" />}
                      {c.to && !last ? (
                        <Link to={c.to} className="truncate text-fg-3 hover:text-fg">
                          {c.label}
                        </Link>
                      ) : (
                        <span className={cn('truncate', last ? 'font-medium text-fg' : 'text-fg-3')} aria-current={last ? 'page' : undefined}>
                          {c.label}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
            <UserMenu />
          </header>
          <main id="conteudo" className="min-w-0 flex-1 px-4 py-6 lg:px-8 lg:py-7">
            <div className="mx-auto max-w-[1360px]">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </CrumbContext.Provider>
  );
}
